import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo } from 'react';
import { getPartyIdFromToken, useSelectedAccount } from '@ibk/shared-auth';
import { selectAccessToken, selectPinCreationHasCheckedPin, selectPinCreationModalOpen, selectPinCreationPreviousAccountId, setHasCheckedPin, setModalOpen, setPreviousAccountId, useAppDispatch, useAppSelector, useCheckHasCredentialsQuery, useCreatePinCredentialsMutation, useHomeProducts, } from '@ibk/shared-config';
import { ApiProductStatus, ProductAlias } from '@ibk/shared-types';
import { PinCreationModal } from '../PinCreationModal';
export const PinCreationProvider = ({ children, enabled = true, onPinCreated, 'data-testid': dataTestId = 'pin-creation-provider', }) => {
    const dispatch = useAppDispatch();
    const isModalOpen = useAppSelector(selectPinCreationModalOpen);
    const hasCheckedPin = useAppSelector(selectPinCreationHasCheckedPin);
    const previousAccountId = useAppSelector(selectPinCreationPreviousAccountId);
    const { selectedAccount } = useSelectedAccount();
    const accessToken = useAppSelector(selectAccessToken);
    const partyId = useMemo(() => {
        if (!accessToken)
            return null;
        return getPartyIdFromToken(accessToken);
    }, [accessToken]);
    const accountId = selectedAccount?.accountId;
    const { products, isLoading: isLoadingProducts } = useHomeProducts({
        enabled: enabled && !!accountId,
    });
    const contaDigitalProduct = products[ProductAlias.CONTA_DIGITAL];
    const hasActiveContaPJ = useMemo(() => {
        return !contaDigitalProduct?.isLoading && contaDigitalProduct?.data?.status === ApiProductStatus.ACTIVE;
    }, [contaDigitalProduct?.isLoading, contaDigitalProduct?.data?.status]);
    const shouldCheckPin = useMemo(() => {
        return enabled && !!accountId && hasActiveContaPJ && !isLoadingProducts;
    }, [enabled, accountId, hasActiveContaPJ, isLoadingProducts]);
    useEffect(() => {
        if (accountId !== previousAccountId) {
            dispatch(setHasCheckedPin({ hasChecked: false }));
            dispatch(setPreviousAccountId({ accountId: accountId ?? null }));
        }
    }, [accountId, previousAccountId, dispatch]);
    const querySkip = !shouldCheckPin || hasCheckedPin || !accountId;
    const { data: hasPin, isLoading: isCheckingPin, isFetching: isFetchingPin, } = useCheckHasCredentialsQuery({ accountId: accountId, partyId: partyId ?? undefined }, {
        skip: querySkip,
        refetchOnFocus: false,
        refetchOnReconnect: false,
        refetchOnMountOrArgChange: true,
    });
    const [createPin, { isLoading: isCreatingPin }] = useCreatePinCredentialsMutation();
    useEffect(() => {
        if (!shouldCheckPin || isCheckingPin || isFetchingPin) {
            return;
        }
        if (!hasCheckedPin && hasPin !== undefined) {
            dispatch(setHasCheckedPin({ hasChecked: true }));
        }
    }, [shouldCheckPin, hasPin, isCheckingPin, isFetchingPin, hasCheckedPin, dispatch]);
    useEffect(() => {
        if (!shouldCheckPin || isCheckingPin || isFetchingPin) {
            return;
        }
        if (hasPin === false && !isModalOpen) {
            dispatch(setModalOpen({ isOpen: true }));
        }
        else if (hasPin === true && isModalOpen) {
            dispatch(setModalOpen({ isOpen: false }));
        }
    }, [shouldCheckPin, hasPin, isCheckingPin, isFetchingPin, isModalOpen, dispatch]);
    const handleCreatePin = useCallback(async (pin) => {
        if (!accountId || !partyId) {
            throw new Error('Dados da conta não disponíveis');
        }
        try {
            await createPin({
                credential: pin,
                partyId,
                accountId,
            }).unwrap();
            dispatch(setHasCheckedPin({ hasChecked: false }));
        }
        catch (error) {
            console.error('[PinCreationProvider] Erro ao criar PIN:', error);
            throw error;
        }
    }, [accountId, partyId, createPin, dispatch]);
    const handleCloseModal = useCallback(() => {
        dispatch(setModalOpen({ isOpen: false }));
        dispatch(setHasCheckedPin({ hasChecked: false }));
        onPinCreated?.();
    }, [onPinCreated, dispatch]);
    return (_jsxs(_Fragment, { children: [children, _jsx(PinCreationModal, { isOpen: isModalOpen, loading: isCreatingPin, onClose: handleCloseModal, onConfirm: handleCreatePin, "data-testid": `${dataTestId}-modal` })] }));
};
export default PinCreationProvider;
